import type { ResponsiveTileTypographyTier } from "../../Utils/useResponsiveTileTypography";

const typographyByTier: Record<
  ResponsiveTileTypographyTier,
  {
    body: string;
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    code: string;
  }
> = {
  compact: {
    body: "12px",
    h1: "14px",
    h2: "12.5px",
    h3: "12px",
    h4: "11.5px",
    code: "11px",
  },
  default: {
    body: "13px",
    h1: "16px",
    h2: "13.5px",
    h3: "13px",
    h4: "12.5px",
    code: "12px",
  },
  comfortable: {
    body: "14px",
    h1: "17px",
    h2: "14.5px",
    h3: "14px",
    h4: "13px",
    code: "12.5px",
  },
};

export const createBestxSummaryHtmlStyles = (
  tier: ResponsiveTileTypographyTier = "default"
) => {
  const typography = typographyByTier[tier];

  return {
    fontSize: typography.body,
    lineHeight: 1.55,
    "& h1": {
      fontSize: typography.h1,
      margin: "0 0 10px 0",
      fontWeight: 800,
    },
    "& h2": {
      fontSize: typography.h2,
      margin: "14px 0 6px 0",
      fontWeight: 800,
    },
    "& h3": {
      fontSize: typography.h3,
      margin: "12px 0 6px 0",
      fontWeight: 800,
    },
    "& h4, & h5, & h6": {
      fontSize: typography.h4,
      margin: "10px 0 4px 0",
      fontWeight: 700,
    },
    "& p": {
      margin: "6px 0",
    },
    "& ul, & ol": {
      margin: "6px 0",
      paddingLeft: "22px",
    },
    "& li": {
      margin: "3px 0",
    },
    "& code": {
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: typography.code,
      background: "#f5f5f5",
      padding: "1px 4px",
      borderRadius: "4px",
    },
  } as const;
};

export const bestxSummaryHtmlStyles = createBestxSummaryHtmlStyles();

/**
 * Minimal markdown -> HTML renderer (no deps)
 * Supports: # to ######, paragraphs, **bold**, *italic*, `code`
 * NOTE: Use only with trusted content (your backend).
 */
export function mdToHtml(md: string): string {
  const escapeHtml = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const applyInline = (s: string) => {
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    return s;
  };

  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let listType: "ul" | "ol" | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = applyInline(escapeHtml(paragraph.join(" ")).trim());
    out.push(`<p>${text}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0 || !listType) return;
    const tag = listType;
    const items = listItems
      .map((item) => `<li>${applyInline(escapeHtml(item))}</li>`)
      .join("\n");
    out.push(`<${tag}>\n${items}\n</${tag}>`);
    listItems = [];
    listType = null;
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const h = applyInline(escapeHtml(text));
      out.push(`<h${level}>${h}</h${level}>`);
      continue;
    }

    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    if (ulMatch && !line.startsWith("**")) {
      flushParagraph();
      if (listType === "ol") flushList();
      listType = "ul";
      listItems.push(ulMatch[1]);
      continue;
    }

    const olMatch = line.match(/^\d+[.)]\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      if (listType === "ul") flushList();
      listType = "ol";
      listItems.push(olMatch[1]);
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return out.join("\n");
}
